import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Loader2, Wifi } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getWifiStats } from '@/api/wifi';
import { Tooltip } from '@/components/ui/tooltip';

/** Статистика → Wi-Fi: кто сидит в сети филиала. Только то, что роутер реально
 *  отдаёт (см. plugins/equipment_discovery/keenetic.py) — без выдуманных «интересов».
 *
 *  Переделано 21.09 (Алексей, AI109): гистограмма по дням отвечала на вопрос,
 *  которого управляющий не задаёт. Ему нужно «когда у нас людно» и «сидят или
 *  забегают», поэтому главным стал час суток, а рядом — день недели и
 *  длительность. Подписи переписаны на человеческие (AI110): прежние говорили
 *  «по MAC, это нижняя граница» — непонятно и вдобавок неверно. */

const KIND_LABEL: Record<string, string> = {
  phone: 'Телефоны', laptop: 'Ноутбуки', tv: 'ТВ и приставки', iot: 'Техника заведения', other: 'Прочее',
};

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export default function AnalyticsWifiTab({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { data, isLoading } = useSWR(
    zoneId ? ['wifi-stats', zoneId, from, to] : null,
    async () => (await getWifiStats(zoneId, from, to)).data,
    { revalidateOnFocus: false },
  );
  if (isLoading || !data) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Загрузка…</div>;
  }
  if (!data.visits) {
    return (
      <Card variant="section">
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
          <Wifi className="size-8 text-muted-foreground" />
          <p className="max-w-md text-sm text-muted-foreground">
            За период роутер не видел ни одного устройства. Если сбор только включили — первые цифры появятся в течение
            получаса.
          </p>
          <Link to="/management/equipment" className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>
            Открыть оборудование
          </Link>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat title="Подключений" value={String(data.visits)}
          hint="сколько раз устройство пришло в сеть и задержалось хотя бы на один опрос" />
        <Stat title="Разных устройств" value={String(data.devices)}
          hint="телефоны время от времени меняют свой сетевой номер, и тогда один гость считается за двух — число скорее завышено" />
        <Stat title="Сидят обычно" value={data.median_minutes !== null ? `${data.median_minutes} мин` : '—'}
          hint="половина подключений короче этого времени, половина длиннее" />
        <Stat title="Приходят снова" value={data.repeat_share !== null ? `${Math.round(data.repeat_share * 100)}%` : '—'}
          hint="доля подключений с устройств, которые здесь уже были; по той же причине со сменой номера это нижняя граница" />
      </div>

      <HourChart byHour={data.by_hour} />

      <div className="grid gap-3 md:grid-cols-2">
        <WeekdayChart byWeekday={data.by_weekday} />
        <Breakdown title="Сколько сидят" rows={data.by_dwell} label={(k) => k} total={data.visits}
          caption="Забегают за кофе с собой или остаются работать." />
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Breakdown title="Устройства" rows={data.by_kind} label={(k) => KIND_LABEL[k] ?? k} total={data.visits} />
        <Breakdown title="Сети" rows={data.by_network} label={(k) => k} total={data.visits} />
        <Breakdown title="Диапазон" rows={data.by_band} label={(k) => (k === 'lan' ? 'Кабель' : `${k} ГГц`)} total={data.visits} />
      </div>
    </div>
  );
}

/** Час суток — главный график вкладки: «когда у нас людно». Считает присутствие,
 *  а не приходы, поэтому гость, просидевший два часа, виден в обоих. */
function HourChart({ byHour }: { byHour: Record<string, number> }) {
  const hours = Array.from({ length: 24 }, (_, h) => ({ h, v: byHour[String(h)] ?? 0 }));
  const peak = Math.max(...hours.map((x) => x.v), 0.01);
  const busiest = hours.reduce((a, b) => (b.v > a.v ? b : a));
  return (
    <Card variant="section">
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Когда людно</div>
          {busiest.v > 0 && (
            <div className="text-xs text-muted-foreground">
              Больше всего около {busiest.h}:00 — в среднем {busiest.v.toFixed(1).replace('.', ',')} устройств
            </div>
          )}
        </div>
        <div className="flex items-end gap-px">
          {hours.map(({ h, v }) => (
            <Tooltip key={h} content={`${h}:00 — в среднем ${v.toFixed(2).replace('.', ',')} устройств в сети`}><div className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <div className={cn('w-full rounded-t', v > 0 ? 'bg-primary/70' : 'bg-muted')}
                style={{ height: `${Math.max(2, (v / peak) * 96)}px` }} />
              {/* Подписываем каждый третий час: на телефоне 24 подписи сливаются. */}
              <span className="text-[10px] leading-none text-muted-foreground">{h % 3 === 0 ? h : ' '}</span>
            </div></Tooltip>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Среднее число устройств в сети в этот час за выбранный период.
        </p>
      </CardContent>
    </Card>
  );
}

function WeekdayChart({ byWeekday }: { byWeekday: Record<string, number> }) {
  const rows = WEEKDAYS.map((label, d) => ({ label, v: byWeekday[String(d)] ?? 0 }));
  const peak = Math.max(...rows.map((r) => r.v), 0.01);
  return (
    <Card variant="section">
      <CardContent className="space-y-2 py-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">По дням недели</div>
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2">
            <span className="w-7 shrink-0 text-xs text-muted-foreground">{r.label}</span>
            <div className="h-2 flex-1 rounded-full bg-muted">
              <div className="h-2 rounded-full bg-primary/70" style={{ width: `${(r.v / peak) * 100}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {r.v.toFixed(1).replace('.', ',')}
            </span>
          </div>
        ))}
        <p className="text-[11px] text-muted-foreground">Подключений в среднем за такой день недели.</p>
      </CardContent>
    </Card>
  );
}

function Stat({ title, value, hint }: { title: string; value: string; hint: string }) {
  return (
    <Card variant="section">
      <CardContent className="space-y-0.5 py-4">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="text-2xl font-semibold">{value}</div>
        <div className="text-[11px] leading-snug text-muted-foreground">{hint}</div>
      </CardContent>
    </Card>
  );
}

function Breakdown({ title, rows, label, total, caption }: {
  title: string; rows: Record<string, number>; label: (k: string) => string; total: number; caption?: string;
}) {
  const entries = Object.entries(rows).filter(([, n]) => n > 0).sort(([, a], [, b]) => b - a);
  return (
    <Card variant="section">
      <CardContent className="space-y-2 py-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
        {entries.map(([k, n]) => (
          <div key={k} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>{label(k)}</span>
              <span className="text-muted-foreground">{Math.round((n / total) * 100)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted">
              <div className="h-1.5 rounded-full bg-primary/70" style={{ width: `${(n / total) * 100}%` }} />
            </div>
          </div>
        ))}
        {caption && <p className="text-[11px] text-muted-foreground">{caption}</p>}
      </CardContent>
    </Card>
  );
}
